import { useCallback, useEffect, useState } from 'react';
import httpClient from '../services/httpClient';

/**
 * useCurrentOrgRole — the current user's role in a given organization.
 *
 * Derives the role from `GET /auth/memberships` (the authenticated user's
 * own memberships, each carrying its role), matched by organizationId.
 * This is preferred over listing an org's members and matching by user id:
 * the org list/detail endpoints don't carry the requesting user's role,
 * and /auth/memberships already returns exactly this user's rows.
 *
 * Returns the role string (e.g. 'OWNER' | 'ADMIN' | 'MEMBER') or null when
 * unknown / not a member / not in an org.
 *
 * @param {string|null} organizationId
 * @param {Object} [options]
 * @param {boolean} [options.auto=true]
 */
export function useCurrentOrgRole(organizationId, { auto = true } = {}) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(Boolean(auto && organizationId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!organizationId) {
      setRole(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await httpClient.get('/auth/memberships');
      // Backend: { status_code, body: { memberships: [{ membership, organization }] } }
      const raw = response.data?.body ?? response.data;
      const memberships = Array.isArray(raw?.memberships) ? raw.memberships : [];
      const mine = memberships.find(
        (m) =>
          String(m.membership?.organization_id ?? m.organization?.id) ===
          String(organizationId)
      );
      setRole(mine?.membership?.role ?? null);
    } catch (err) {
      setRole(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (auto && organizationId) {
      refetch();
    } else if (!organizationId) {
      setRole(null);
      setError(null);
      setLoading(false);
    }
  }, [auto, organizationId, refetch]);

  return { role, loading, error, refetch };
}

export default useCurrentOrgRole;
